"use client";

import { useEffect, useRef, type RefObject } from "react";

import { modalStack } from "@/fetch-ui/FetchModal";

/** What dismissed the layer: the Escape key, or a press outside its container. */
export type DismissReason = "escape" | "outside";

interface UseDismissableLayerOptions {
  /** Whether the layer is open. Nothing is registered or listened to while it is not. */
  isActive: boolean;
  /** Called when the layer should close. May be a new function every render. */
  onDismiss: (reason: DismissReason) => void;
  /** Counts as inside too, when it lives outside the container: a trigger that toggles the layer itself. */
  ignoreRef?: RefObject<HTMLElement | null>;
}

/**
 * The dismiss behavior of a popover that opens inside a modal: Escape closes only the popover, and
 * a press outside it closes it too.
 *
 * While active it registers on the shared {@link modalStack}, which is what makes the surrounding
 * modal stand down on Escape (a modal closes only when it is the topmost surface). It then closes
 * on Escape itself, and only while it is still topmost, so a surface opened above it gets the key
 * first. Hooks that only close on an outside press, such as `useClickOutside`, leave the modal to
 * close on the same Escape.
 *
 * The reason is passed along because the two dismissals differ in what should follow: after Escape
 * focus usually goes back to the trigger, after a press elsewhere it must stay where the person put
 * it.
 *
 * @param options - See {@link UseDismissableLayerOptions}.
 * @returns A ref to attach to the layer's container, and to whatever should count as inside it (a
 *   trigger, for one).
 */
export function useDismissableLayer<T extends HTMLElement>({
  isActive,
  onDismiss,
  ignoreRef,
}: UseDismissableLayerOptions): RefObject<T> {
  // Hooks
  const containerRef = useRef<T>(null);
  // The latest callback, read when an event fires, so a new function each render does not tear the
  // listeners down and put them back on the stack.
  const onDismissRef = useRef(onDismiss);

  // Effects
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!isActive) return;

    const sentinel = (): void => onDismissRef.current("escape");
    modalStack.push(sentinel);

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && modalStack.isTop(sentinel)) onDismissRef.current("escape");
    };
    const handlePointerDown = (event: PointerEvent): void => {
      if (!(event.target instanceof Node) || ignoreRef?.current?.contains(event.target)) return;
      if (!containerRef.current?.contains(event.target)) {
        onDismissRef.current("outside");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
      modalStack.remove(sentinel);
    };
  }, [isActive, ignoreRef]);

  return containerRef;
}
